import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, combineLatest, timer, of } from 'rxjs';
import { catchError, tap, map, switchMap, distinctUntilChanged, filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface ScanSession {
  sessionId: string;
  orderId: string;
  preAlertId?: string;
  expectedCartons: string[];
  scannedCartons: string[];
  sessionType: 'mobile_scan' | 'warehouse_scan';
  status: 'active' | 'paused' | 'completed' | 'cancelled';
  startTime: Date;
  endTime?: Date;
  progress: {
    scannedCount: number;
    expectedCount: number;
    matchedCount: number;
    unexpectedCount: number;
    shortageCount: number;
    completionPercentage: number;
  };
}

export interface CartonScanRequest {
  sessionId: string;
  cartonNo: string;
  scannedBy: string;
  timestamp: Date;
  location?: string;
  notes?: string;
}

export interface CartonScanResponse {
  success: boolean;
  cartonNo: string;
  isExpected: boolean;
  status: 'matched' | 'unexpected' | 'duplicate' | 'invalid' | 'arrived';
  message: string;
  session: ScanSession;
}

export interface StartSessionRequest {
  orderId: string;
  sessionType: 'mobile_scan' | 'warehouse_scan';
  expectedCartons: string[];
  scannedBy: string;
  location?: string;
}

export interface ScanSessionSummary {
  sessionId: string;
  totalScanned: number;
  matchedCartons: number;
  unexpectedCartons: number;
  missingCartons: string[];
  shortages: string[];
  overages: string[];
  success: boolean;
  completedAt: Date;
}

// === NEW ORDER STATUS INTEGRATION INTERFACES ===

export interface ScanWithStatusResult {
  success: boolean;
  cartonNo: string;
  status: 'matched' | 'unexpected' | 'duplicate' | 'invalid' | 'arrived';
  message: string;
  orderStatusUpdated: boolean;
  preAlertStatusUpdated: boolean;
  newOrderStatus?: string;
  completionProgress: {
    scannedCount: number;
    totalExpected: number;
    completionPercentage: number;
  };
  impactAnalysis: {
    ordersAffected: number;
    preAlertsAffected: number;
    statusTransitions: string[];
  };
}

export interface ScanImpactValidation {
  cartonId: string;
  isValid: boolean;
  expectedOutcome: {
    orderStatusChange?: string;
    preAlertProgress?: number;
    workflowAdvancement?: boolean;
  };
  warnings: string[];
  recommendations: string[];
}

export interface ScanningProgress {
  preAlertId: string;
  ladingBill: string;
  totalCartons: number;
  scannedCartons: number;
  completionPercentage: number;
  status: 'Created' | 'PartialManifest' | 'CompleteManifest';
  estimatedCompletion?: Date;
  recentActivity: {
    lastScannedCarton?: string;
    lastScanTime?: Date;
    scanningRate?: number; // cartons per hour
  };
}

export interface OrderScanningStatus {
  orderId: string;
  currentStatus: string;
  canAdvance: boolean;
  linkedPreAlerts: {
    preAlertId: string;
    ladingBill: string;
    completionPercentage: number;
    isComplete: boolean;
  }[];
  overallProgress: {
    totalPreAlerts: number;
    completedPreAlerts: number;
    overallCompletionPercentage: number;
  };
  nextStatusIfComplete?: string;
}

export interface PreAlertScanningMetrics {
  ladingBill: string;
  totalCartons: number;
  scannedCartons: number;
  lastActivity: Date;
  completionPercentage: number;
  estimatedTimeToCompletion?: number; // minutes
  scanningVelocity: {
    cartonsPerHour: number;
    timeRemaining: number; // minutes
  };
  issues: {
    duplicateScans: number;
    unexpectedCartons: number;
    missingCartons: string[];
  };
  cartonDetails?: Array<{
    carrierTrackingNumber: string;
    cartonNumber: string;
    orderId: string;
    isScanned: boolean;
    currentStatus: string;
    lastScanTime?: string;
    scannedBy?: string;
  }>;
}

// === NEW CARTON LIST INTERFACES ===

export interface CartonListItem {
  cartonNumber: string;
  isScanned: boolean;
  scannedAt?: Date;
  scannedBy?: string;
  scanLocation?: string;
  orderId?: string;
  preAlertId?: string;
  ladingBill?: string;
  status: 'pending' | 'scanned' | 'unexpected' | 'duplicate';
  errorMessage?: string;
}

export interface PreAlertCartonList {
  ladingBill: string;
  preAlertId: string;
  totalCartons: number;
  scannedCount: number;
  unscannedCount: number;
  completionPercentage: number;
  cartons: CartonListItem[];
  lastUpdated: Date;
  orderInfo?: {
    orderId: string;
    orderStatus: string;
  };
}

export interface CartonListFilter {
  status?: 'all' | 'scanned' | 'unscanned' | 'unexpected';
  searchTerm?: string;
  sortBy?: 'cartonNumber' | 'scannedAt' | 'status';
  sortDirection?: 'asc' | 'desc';
  showOnlyRecentScans?: boolean;
  recentScanHours?: number;
}

export interface CartonScanSummary {
  preAlertId: string;
  ladingBill: string;
  totalCartons: number;
  scannedCartons: number;
  recentlyScanned: CartonListItem[];
  unscannedCartons: CartonListItem[];
  issues: {
    duplicates: CartonListItem[];
    unexpected: CartonListItem[];
    errors: CartonListItem[];
  };
}

// === AUTO-DETECTION INTERFACES ===

export interface PreAlertDetectionResult {
  found: boolean;
  message: string;
  preAlert?: PreAlertInfo;
  cartons: CartonDetectionInfo[];
  scannedCarton: string;
}

export interface PreAlertInfo {
  id: string;
  ladingBill: string;
  transportCompany: string;
  arrivalLocation: string;
  departureLocation: string;
  eta?: Date;
  vesselNumber: string;
  status: string;
  totalCartons: number;
}

export interface CartonDetectionInfo {
  cartonNumber: string;
  carrierTrackingNumber: string;
  orderId: string;
  isScanned: boolean;
}

// === BULK UPDATE INTERFACES ===

export interface BulkStatusUpdateRequest {
  cartonNumbers: string[];
  sessionId: string;
  updatedBy: string;
  ladingBill?: string;
}

export interface BulkUpdateResult {
  totalRequested: number;
  successfulUpdates: number;
  skippedAlreadyScanned: number;
  failedUpdates: number;
  failedCartons: FailedCartonUpdate[];
  allSuccessful: boolean;
  processedAt: Date;
  message: string;
}

export interface FailedCartonUpdate {
  cartonNumber: string;
  errorMessage: string;
  retryCount: number;
  failedAt: Date;
}

export interface ApiResponse<T> {
  retcode: number;
  message: string;
  data: T;
}

@Injectable({
  providedIn: 'root'
})
export class MobileScanningService {
  private apiUrl = `${environment.apiUrl}/mobile-scanning`;
  
  // Session state management
  private currentSessionSubject = new BehaviorSubject<ScanSession | null>(null);
  public currentSession$ = this.currentSessionSubject.asObservable();
  
  // Local storage keys
  private readonly SESSION_STORAGE_KEY = 'mobile_scanning_session';
  private readonly SCAN_HISTORY_KEY = 'mobile_scanning_history';

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {
    this.loadSessionFromStorage();
  }

  // === NEW ORDER STATUS INTEGRATION METHODS ===

  /**
   * Enhanced carton scanning with automatic order status updates
   * Connects existing scanning with new backend order workflow integration
   */
  scanCartonWithStatusUpdate(scanData: CartonScanRequest): Observable<ScanWithStatusResult> {
    return this.http.post<ScanWithStatusResult>(`${this.apiUrl}/scan-with-status-update`, {
      sessionId: scanData.sessionId,
      cartonNo: scanData.cartonNo,
      scannedBy: scanData.scannedBy,
      timestamp: scanData.timestamp.toISOString(),
      location: scanData.location,
      notes: scanData.notes
    }).pipe(
      tap(result => {
        if (result.success) {
          console.log('Carton scan successful with status updates:', {
            carton: result.cartonNo,
            orderUpdated: result.orderStatusUpdated,
            preAlertUpdated: result.preAlertStatusUpdated,
            progress: result.completionProgress
          });
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Validate the impact of scanning a specific carton before actual scan
   * Provides preview of what changes will occur
   */
  validateScanImpact(cartonId: string, context: string): Observable<ScanImpactValidation> {
    return this.http.post<ScanImpactValidation>(`${this.apiUrl}/validate-scan-impact`, {
      cartonId,
      context
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get real-time scanning progress for a specific pre-alert
   * Includes completion metrics and scanning velocity
   */
  getScanningProgress(preAlertId: string): Observable<ScanningProgress> {
    return this.http.get<ScanningProgress>(`${this.apiUrl}/scanning-progress/${preAlertId}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get order-level scanning completion status
   * Shows progress across all linked pre-alerts
   */
  getOrderScanningStatus(orderId: string): Observable<OrderScanningStatus> {
    return this.http.get<OrderScanningStatus>(`${environment.apiUrl}/orders/${orderId}/scanning-completion-status`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get detailed scanning metrics for a pre-alert
   * Includes velocity analysis and issue detection
   */
  getPreAlertScanningMetrics(ladingBill: string): Observable<PreAlertScanningMetrics> {
    return this.http.get<PreAlertScanningMetrics>(`${environment.apiUrl}/pre-alerts/${ladingBill}/scanning-metrics`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get bulk scanning metrics for dashboard display
   * Useful for real-time progress tracking across multiple pre-alerts
   */
  getBulkScanningMetrics(preAlertIds: string[]): Observable<PreAlertScanningMetrics[]> {
    return this.http.post<PreAlertScanningMetrics[]>(`${environment.apiUrl}/pre-alerts/bulk-scanning-metrics`, {
      preAlertIds
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Update order status based on scanning completion
   * Triggers workflow advancement when scanning is complete
   */
  updateOrderStatusFromScan(orderId: string, scanContext: any): Observable<{
    success: boolean;
    newStatus: string;
    message: string;
  }> {
    return this.http.post<any>(`${environment.apiUrl}/orders/${orderId}/update-status-from-scan`, scanContext).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Update pre-alert scanning progress
   * Triggers status transitions and completion analysis
   */
  updatePreAlertScanningProgress(ladingBill: string, progressData: any): Observable<{
    success: boolean;
    newStatus: string;
    completionPercentage: number;
    message: string;
  }> {
    return this.http.post<any>(`${environment.apiUrl}/pre-alerts/${ladingBill}/update-scanning-progress`, progressData).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Start a new scanning session
   */
  startScanSession(request: StartSessionRequest): Observable<ScanSession> {
    return this.http.post<ScanSession>(`${this.apiUrl}/start-session`, request).pipe(
      tap(session => {
        this.setCurrentSession(session);
        this.saveSessionToStorage(session);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Scan a carton and update session
   */
  scanCarton(request: CartonScanRequest): Observable<CartonScanResponse> {
    return this.http.post<CartonScanResponse>(`${this.apiUrl}/scan-carton`, request).pipe(
      tap(response => {
        if (response.success && response.session) {
          this.setCurrentSession(response.session);
          this.saveSessionToStorage(response.session);
          this.addToScanHistory(request);
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Complete the current scanning session
   */
  completeScanSession(sessionId: string, notes?: string): Observable<ScanSessionSummary> {
    return this.http.post<ScanSessionSummary>(`${this.apiUrl}/complete-session`, {
      sessionId,
      notes
    }).pipe(
      tap(summary => {
        if (summary.success) {
          this.clearCurrentSession();
          this.clearSessionFromStorage();
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Pause the current scanning session
   */
  pauseScanSession(sessionId: string): Observable<ScanSession> {
    return this.http.post<ScanSession>(`${this.apiUrl}/pause-session`, { sessionId }).pipe(
      tap(session => {
        this.setCurrentSession(session);
        this.saveSessionToStorage(session);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Resume a paused scanning session
   */
  resumeScanSession(sessionId: string): Observable<ScanSession> {
    return this.http.post<ScanSession>(`${this.apiUrl}/resume-session`, { sessionId }).pipe(
      tap(session => {
        this.setCurrentSession(session);
        this.saveSessionToStorage(session);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Get current session status
   */
  getSessionStatus(sessionId: string): Observable<ScanSession> {
    return this.http.get<ScanSession>(`${this.apiUrl}/session/${sessionId}`).pipe(
      tap(session => {
        this.setCurrentSession(session);
        this.saveSessionToStorage(session);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Get scanning history for analysis
   */
  getScanHistory(sessionId?: string, limit: number = 100): Observable<CartonScanRequest[]> {
    const params: any = { limit };
    if (sessionId) {
      params.sessionId = sessionId;
    }

    return this.http.get<CartonScanRequest[]>(`${this.apiUrl}/scan-history`, { params }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Validate carton list for expected items
   */
  validateExpectedCartons(orderId: string, cartons: string[]): Observable<{
    valid: string[];
    invalid: string[];
    duplicates: string[];
    totalExpected: number;
  }> {
    return this.http.post<any>(`${this.apiUrl}/validate-cartons`, {
      orderId,
      cartons
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Local session management
   */
  getCurrentSession(): ScanSession | null {
    return this.currentSessionSubject.value;
  }

  setCurrentSession(session: ScanSession | null): void {
    this.currentSessionSubject.next(session);
  }

  clearCurrentSession(): void {
    this.currentSessionSubject.next(null);
  }

  hasActiveSession(): boolean {
    const session = this.getCurrentSession();
    return session !== null && session.status === 'active';
  }

  /**
   * Offline/Local storage management
   */
  private saveSessionToStorage(session: ScanSession): void {
    try {
      localStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify({
        ...session,
        startTime: session.startTime.toISOString(),
        endTime: session.endTime?.toISOString()
      }));
    } catch (error) {
      console.warn('Failed to save session to local storage:', error);
    }
  }

  private loadSessionFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.SESSION_STORAGE_KEY);
      if (stored) {
        const session = JSON.parse(stored);
        // Convert ISO strings back to Date objects
        session.startTime = new Date(session.startTime);
        if (session.endTime) {
          session.endTime = new Date(session.endTime);
        }
        
        // Only restore active sessions
        if (session.status === 'active' || session.status === 'paused') {
          this.setCurrentSession(session);
        }
      }
    } catch (error) {
      console.warn('Failed to load session from local storage:', error);
    }
  }

  private clearSessionFromStorage(): void {
    try {
      localStorage.removeItem(this.SESSION_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear session from local storage:', error);
    }
  }

  private addToScanHistory(scan: CartonScanRequest): void {
    try {
      const history = this.getLocalScanHistory();
      history.unshift({
        ...scan,
        timestamp: scan.timestamp.toISOString()
      } as any);
      
      // Keep only last 500 scans
      const trimmedHistory = history.slice(0, 500);
      localStorage.setItem(this.SCAN_HISTORY_KEY, JSON.stringify(trimmedHistory));
    } catch (error) {
      console.warn('Failed to save scan to local history:', error);
    }
  }

  getLocalScanHistory(): any[] {
    try {
      const stored = localStorage.getItem(this.SCAN_HISTORY_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to load scan history from local storage:', error);
      return [];
    }
  }

  clearLocalScanHistory(): void {
    try {
      localStorage.removeItem(this.SCAN_HISTORY_KEY);
    } catch (error) {
      console.warn('Failed to clear scan history from local storage:', error);
    }
  }

  /**
   * Utility methods
   */
  calculateProgress(session: ScanSession): {
    scannedCount: number;
    expectedCount: number;
    matchedCount: number;
    unexpectedCount: number;
    shortageCount: number;
    completionPercentage: number;
  } {
    const expectedCount = session.expectedCartons.length;
    const scannedCartons = session.scannedCartons;
    const scannedCount = scannedCartons.length;
    const matchedCartons = scannedCartons.filter(carton => 
      session.expectedCartons.includes(carton)
    );
    const matchedCount = matchedCartons.length;
    const unexpectedCount = scannedCount - matchedCount;
    const shortageCount = Math.max(0, expectedCount - matchedCount);
    const completionPercentage = expectedCount > 0 
      ? Math.round((matchedCount / expectedCount) * 100)
      : 0;

    return {
      scannedCount,
      expectedCount,
      matchedCount,
      unexpectedCount,
      shortageCount,
      completionPercentage
    };
  }

  getMissingCartons(session: ScanSession): string[] {
    return session.expectedCartons.filter(expected => 
      !session.scannedCartons.includes(expected)
    );
  }

  getUnexpectedCartons(session: ScanSession): string[] {
    return session.scannedCartons.filter(scanned => 
      !session.expectedCartons.includes(scanned)
    );
  }

  // === ENHANCED SCANNING WORKFLOW INTEGRATION ===

  /**
   * Enhanced scanning method that integrates with order status workflow
   * This replaces the basic scanCarton method for status-aware scanning
   */
  scanCartonWithWorkflowIntegration(request: CartonScanRequest): Observable<ScanWithStatusResult> {
    // First validate the scan impact
    return this.validateScanImpact(request.cartonNo, request.sessionId).pipe(
      switchMap(validation => {
        if (!validation.isValid) {
          return throwError(() => new Error(`Scan validation failed: ${validation.warnings.join(', ')}`));
        }

        // Proceed with enhanced scanning
        return this.scanCartonWithStatusUpdate(request);
      }),
      tap(result => {
        // Update local session state if needed
        if (result.success && this.getCurrentSession()) {
          const session = this.getCurrentSession()!;
          // Update session progress with new information
          this.updateSessionProgressFromScanResult(session, result);
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Get comprehensive scanning status for multiple orders
   * Useful for dashboard views and progress monitoring
   */
  getMultiOrderScanningStatus(orderIds: string[]): Observable<OrderScanningStatus[]> {
    const requests = orderIds.map(orderId => this.getOrderScanningStatus(orderId));
    return combineLatest(requests).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Real-time scanning progress monitoring
   * Returns an observable that emits progress updates
   */
  monitorScanningProgress(preAlertId: string, intervalMs: number = 30000): Observable<ScanningProgress> {
    return timer(0, intervalMs).pipe(
      switchMap(() => this.getScanningProgress(preAlertId)),
      distinctUntilChanged((prev, curr) =>
        prev.scannedCartons === curr.scannedCartons &&
        prev.completionPercentage === curr.completionPercentage
      ),
      catchError(error => {
        console.warn('Progress monitoring error:', error);
        return of(null as any);
      }),
      filter(progress => progress !== null)
    );
  }

  /**
   * Automated workflow advancement check
   * Checks if orders can advance based on scanning completion
   */
  checkWorkflowAdvancement(orderId: string): Observable<{
    canAdvance: boolean;
    nextStatus?: string;
    blockers: string[];
    progress: number;
  }> {
    return this.getOrderScanningStatus(orderId).pipe(
      map(status => ({
        canAdvance: status.canAdvance,
        nextStatus: status.nextStatusIfComplete,
        blockers: status.linkedPreAlerts
          .filter(pa => !pa.isComplete)
          .map(pa => `Pre-alert ${pa.ladingBill} at ${pa.completionPercentage}%`),
        progress: status.overallProgress.overallCompletionPercentage
      })),
      catchError(this.handleError)
    );
  }

  /**
   * Batch status update for multiple scanned cartons
   * Optimizes API calls when processing multiple scans
   */
  batchUpdateScanStatus(scanRequests: CartonScanRequest[]): Observable<ScanWithStatusResult[]> {
    return this.http.post<ScanWithStatusResult[]>(`${this.apiUrl}/batch-scan-with-status-update`, {
      scans: scanRequests.map(req => ({
        sessionId: req.sessionId,
        cartonNo: req.cartonNo,
        scannedBy: req.scannedBy,
        timestamp: req.timestamp.toISOString(),
        location: req.location,
        notes: req.notes
      }))
    }).pipe(
      catchError(this.handleError)
    );
  }

  // === CARTON LIST MANAGEMENT ===

  /**
   * Get detailed carton list for a pre-alert showing scanned/unscanned status
   * Transforms PreAlertScanningMetrics into structured carton list
   */
  getPreAlertCartonList(ladingBill: string): Observable<PreAlertCartonList> {
    return this.getPreAlertScanningMetrics(ladingBill).pipe(
      map(metrics => this.transformMetricsToCartonList(metrics)),
      catchError(this.handleError)
    );
  }

  /**
   * Get filtered and sorted carton list for a pre-alert
   */
  getFilteredCartonList(ladingBill: string, filter: CartonListFilter): Observable<CartonListItem[]> {
    return this.getPreAlertCartonList(ladingBill).pipe(
      map(cartonList => this.applyCartonListFilter(cartonList.cartons, filter)),
      catchError(this.handleError)
    );
  }

  /**
   * Get carton scan summary with recently scanned and remaining cartons
   */
  getCartonScanSummary(ladingBill: string): Observable<CartonScanSummary> {
    return this.getPreAlertCartonList(ladingBill).pipe(
      map(cartonList => this.createCartonScanSummary(cartonList)),
      catchError(this.handleError)
    );
  }

  /**
   * Monitor carton list changes in real-time
   */
  monitorCartonList(ladingBill: string, intervalMs: number = 15000): Observable<PreAlertCartonList> {
    return timer(0, intervalMs).pipe(
      switchMap(() => this.getPreAlertCartonList(ladingBill)),
      distinctUntilChanged((prev, curr) =>
        prev.scannedCount === curr.scannedCount &&
        prev.lastUpdated.getTime() === curr.lastUpdated.getTime()
      ),
      catchError(error => {
        console.warn('Carton list monitoring error:', error);
        return of(null as any);
      }),
      filter(cartonList => cartonList !== null)
    );
  }

  /**
   * Get multiple carton lists for dashboard view
   */
  getMultipleCartonLists(ladingBills: string[]): Observable<PreAlertCartonList[]> {
    const requests = ladingBills.map(bill => this.getPreAlertCartonList(bill));
    return combineLatest(requests).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Search for cartons across multiple pre-alerts
   */
  searchCartonsAcrossPreAlerts(searchTerm: string, ladingBills: string[]): Observable<CartonListItem[]> {
    return this.getMultipleCartonLists(ladingBills).pipe(
      map(cartonLists => {
        const allCartons: CartonListItem[] = [];
        cartonLists.forEach(list => allCartons.push(...list.cartons));
        return allCartons.filter(carton =>
          carton.cartonNumber.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }),
      catchError(this.handleError)
    );
  }

  // === CARTON LIST HELPER METHODS ===

  /**
   * Transform PreAlertScanningMetrics to structured carton list
   */
  private transformMetricsToCartonList(metrics: PreAlertScanningMetrics): PreAlertCartonList {
    let allCartons: CartonListItem[] = [];

    // Use actual carton details from API if available
    if (metrics.cartonDetails && metrics.cartonDetails.length > 0) {
      allCartons = metrics.cartonDetails.map(detail => ({
        cartonNumber: detail.cartonNumber,
        isScanned: detail.isScanned,
        scannedAt: detail.lastScanTime ? new Date(detail.lastScanTime) : undefined,
        scannedBy: detail.scannedBy || (detail.isScanned ? 'Unknown' : undefined),
        orderId: detail.orderId,
        status: detail.isScanned ? ('scanned' as const) : ('pending' as const)
      }));
    } else {
      // Fallback: Create scanned cartons list (these would come from scan history)
      const scannedCartons: CartonListItem[] = Array.from({ length: metrics.scannedCartons }, (_, index) => ({
        cartonNumber: `CTN${String(index + 1).padStart(6, '0')}`, // Placeholder - should come from actual scan data
        isScanned: true,
        scannedAt: new Date(Date.now() - (Math.random() * 86400000)), // Last 24 hours
        scannedBy: 'warehouse_user',
        status: 'scanned' as const
      }));

      // Create unscanned cartons from missing cartons list
      const unscannedCartons: CartonListItem[] = metrics.issues.missingCartons.map(cartonNumber => ({
        cartonNumber,
        isScanned: false,
        status: 'pending' as const
      }));

      // Combine all cartons
      allCartons = [...scannedCartons, ...unscannedCartons];
    }

    return {
      ladingBill: metrics.ladingBill,
      preAlertId: `PA-${metrics.ladingBill}`, // Assuming pattern
      totalCartons: metrics.totalCartons,
      scannedCount: metrics.scannedCartons,
      unscannedCount: metrics.totalCartons - metrics.scannedCartons,
      completionPercentage: metrics.completionPercentage,
      cartons: allCartons,
      lastUpdated: metrics.lastActivity
    };
  }

  /**
   * Apply filtering and sorting to carton list
   */
  private applyCartonListFilter(cartons: CartonListItem[], filter: CartonListFilter): CartonListItem[] {
    let filtered = [...cartons];

    // Apply status filter
    if (filter.status && filter.status !== 'all') {
      switch (filter.status) {
        case 'scanned':
          filtered = filtered.filter(c => c.isScanned);
          break;
        case 'unscanned':
          filtered = filtered.filter(c => !c.isScanned);
          break;
        case 'unexpected':
          filtered = filtered.filter(c => c.status === 'unexpected');
          break;
      }
    }

    // Apply search filter
    if (filter.searchTerm) {
      const term = filter.searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.cartonNumber.toLowerCase().includes(term) ||
        c.scannedBy?.toLowerCase().includes(term)
      );
    }

    // Apply recent scans filter
    if (filter.showOnlyRecentScans && filter.recentScanHours) {
      const cutoffTime = new Date(Date.now() - (filter.recentScanHours * 60 * 60 * 1000));
      filtered = filtered.filter(c =>
        c.scannedAt && c.scannedAt >= cutoffTime
      );
    }

    // Apply sorting
    if (filter.sortBy) {
      filtered.sort((a, b) => {
        let aValue: any, bValue: any;

        switch (filter.sortBy) {
          case 'cartonNumber':
            aValue = a.cartonNumber;
            bValue = b.cartonNumber;
            break;
          case 'scannedAt':
            aValue = a.scannedAt?.getTime() || 0;
            bValue = b.scannedAt?.getTime() || 0;
            break;
          case 'status':
            aValue = a.status;
            bValue = b.status;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return filter.sortDirection === 'desc' ? 1 : -1;
        }
        if (aValue > bValue) {
          return filter.sortDirection === 'desc' ? -1 : 1;
        }
        return 0;
      });
    }

    return filtered;
  }

  /**
   * Create carton scan summary for quick overview
   */
  private createCartonScanSummary(cartonList: PreAlertCartonList): CartonScanSummary {
    const now = new Date();
    const recentCutoff = new Date(now.getTime() - (2 * 60 * 60 * 1000)); // Last 2 hours

    return {
      preAlertId: cartonList.preAlertId,
      ladingBill: cartonList.ladingBill,
      totalCartons: cartonList.totalCartons,
      scannedCartons: cartonList.scannedCount,
      recentlyScanned: cartonList.cartons
        .filter(c => c.isScanned && c.scannedAt && c.scannedAt >= recentCutoff)
        .sort((a, b) => (b.scannedAt?.getTime() || 0) - (a.scannedAt?.getTime() || 0))
        .slice(0, 5), // Most recent 5
      unscannedCartons: cartonList.cartons.filter(c => !c.isScanned),
      issues: {
        duplicates: cartonList.cartons.filter(c => c.status === 'duplicate'),
        unexpected: cartonList.cartons.filter(c => c.status === 'unexpected'),
        errors: cartonList.cartons.filter(c => c.errorMessage)
      }
    };
  }

  /**
   * Update carton status to "Arrived" when scanned by user
   */
  updateCartonStatusToArrived(cartonNo: string): Observable<any> {
    // Triple fallback to ensure we get username from any available source
    const currentUser = this.authService.getCurrentUser()?.username ||
                        this.authService.getUsername() ||
                        'Unknown';
    return this.http.post(`${environment.apiUrl}/pre-alerts/update-carton-status/${cartonNo}/arrived`, {
      scannedBy: currentUser
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Batch update carton status (simplified version for queued updates)
   * Updates multiple cartons to "Arrived" status in a single API call
   */
  batchUpdateCartonStatus(
    cartonNumbers: string[],
    sessionId?: string,
    ladingBill?: string
  ): Observable<BulkUpdateResult> {
    const currentUser = this.authService.getCurrentUser()?.username ||
                        this.authService.getUsername() ||
                        'Unknown';

    // Use current session ID if not provided
    const activeSessionId = sessionId || this.getCurrentSession()?.sessionId || '00000000-0000-0000-0000-000000000000';

    return this.http.post<ApiResponse<BulkUpdateResult>>(
      `${environment.apiUrl}/pre-alerts/batch-update-status`,
      {
        CartonNumbers: cartonNumbers,      // Backend expects PascalCase
        SessionId: activeSessionId,        // REQUIRED by backend
        UpdatedBy: currentUser,            // Backend expects UpdatedBy (not scannedBy)
        LadingBill: ladingBill || ''       // Optional context
      }
    ).pipe(
      map((response: any) => {
        // Handle both ApiResponse format and direct BulkUpdateResult format
        return response.data || response;
      }),
      catchError(this.handleError)
    );
  }

  // === PRIVATE HELPER METHODS ===

  private updateSessionProgressFromScanResult(session: ScanSession, result: ScanWithStatusResult): void {
    // Update session progress with enhanced status information
    session.progress = {
      ...session.progress,
      scannedCount: result.completionProgress.scannedCount,
      completionPercentage: result.completionProgress.completionPercentage
    };

    // Add scanned carton to session if not already present
    if (!session.scannedCartons.includes(result.cartonNo)) {
      session.scannedCartons.push(result.cartonNo);
    }

    // Update session storage
    this.setCurrentSession(session);
    this.saveSessionToStorage(session);
  }

  // === AUTO-DETECTION METHODS ===

  /**
   * Find pre-alert by carton number for auto-detection
   * Calls the backend API to detect which pre-alert contains the scanned carton
   */
  findPreAlertByCarton(cartonNo: string): Observable<PreAlertDetectionResult> {
    return this.http.get<PreAlertDetectionResult>(`${environment.apiUrl}/pre-alerts/find-by-carton/${encodeURIComponent(cartonNo)}`).pipe(
      tap(result => {
        if (result.found) {
          console.log(`Auto-detected pre-alert ${result.preAlert?.ladingBill} for carton ${cartonNo}`);
        } else {
          console.warn(`No pre-alert found for carton ${cartonNo}`);
        }
      }),
      catchError(this.handleError)
    );
  }

  // === BULK UPDATE METHODS ===

  /**
   * Bulk update carton scan status for multiple cartons
   * Used when completing mobile scan sessions to ensure all scanned cartons are updated atomically
   * @param cartonNumbers Array of carton numbers to update
   * @param sessionId Current scan session ID
   * @param ladingBill Optional lading bill number for context
   * @returns Detailed results of the bulk update operation
   */
  bulkUpdateCartonStatus(
    cartonNumbers: string[],
    sessionId: string,
    ladingBill?: string
  ): Observable<BulkUpdateResult> {
    const currentUser = this.authService.getCurrentUser();
    const userId = currentUser?.username || 'unknown';

    const request: BulkStatusUpdateRequest = {
      cartonNumbers,
      sessionId,
      updatedBy: userId,
      ladingBill
    };

    return this.http.post<ApiResponse<BulkUpdateResult>>(
      `${this.apiUrl}/bulk-update-status`,
      request
    ).pipe(
      map(response => response.data),
      tap(result => {
        console.log(`Bulk update completed: ${result.successfulUpdates} successful, ${result.skippedAlreadyScanned} skipped, ${result.failedUpdates} failed`);
      }),
      catchError(this.handleError)
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('Mobile Scanning API Error:', error);

    let errorMessage = 'An error occurred during mobile scanning operation';

    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 0) {
      errorMessage = 'Unable to connect to the scanning service. Please check your internet connection.';
    } else if (error.status >= 400 && error.status < 500) {
      errorMessage = 'Invalid scanning request. Please check your input and try again.';
    } else if (error.status >= 500) {
      errorMessage = 'Scanning service is temporarily unavailable. Please try again later.';
    }

    return throwError(() => new Error(errorMessage));
  }
}